import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { shouldShowAppleButton } from '../lib/appleAuth';

// Brand assets
const BRAND_LOGO = "/brand/byard-logo.svg";
const BRAND_ACRONYM = "/brand/byard-logo-acronym.jpg";

// Map Firebase auth error codes to friendly user-facing messages.
const FB_ERROR_MAP = {
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/user-not-found': 'No account exists for this email.',
  'auth/user-disabled': 'This account has been disabled. Contact support.',
  'auth/wrong-password': 'Incorrect password.',
  // Firebase SDK v9.20+ collapses wrong-email AND wrong-password into one
  // generic code (email-enumeration protection). Both codes below appear
  // depending on the SDK version — message stays the same.
  'auth/invalid-credential': 'Incorrect email or password. Please try again.',
  'auth/invalid-login-credentials': 'Incorrect email or password. Please try again.',
  'auth/email-already-in-use': 'An account with this email already exists.',
  'auth/weak-password': 'Password must be at least 6 characters.',
  'auth/too-many-requests': 'Too many failed attempts. Please wait a few minutes and try again.',
  'auth/popup-closed-by-user': 'Google sign-in cancelled.',
  'auth/popup-blocked': 'Popup blocked — please allow popups for this site.',
  'auth/network-request-failed': 'Network error. Please check your connection and try again.',
  'auth/operation-not-allowed': 'Email/password sign-in is disabled. Contact support.',
  'auth/unauthorized-domain': 'This domain is not authorized for sign-in. Contact support.',
};

// Pull a Firebase-style code out of various places it can hide.
function extractFirebaseCode(err) {
  if (err?.code && /^auth\//.test(err.code)) return err.code;
  const msg = err?.message || '';
  // Firebase's stringified error often looks like:
  //   "Firebase: Error (auth/invalid-credential)."
  //   "Firebase: Error (auth/wrong-password)."
  const m = msg.match(/\(auth\/[a-z-]+\)/i);
  if (m) return m[0].slice(1, -1);
  return null;
}

function friendlyError(err) {
  // 1) Try mapped Firebase code (direct property or extracted from message)
  const code = extractFirebaseCode(err);
  if (code && FB_ERROR_MAP[code]) return FB_ERROR_MAP[code];

  // 2) Backend-shaped errors (axios → FastAPI) carry the message in
  //    err.response.data.detail
  if (err?.response?.data?.detail) {
    const detail = err.response.data.detail;
    return typeof detail === 'string' ? detail : 'Something went wrong. Please try again.';
  }

  // 3) Pure network failures (axios "Network Error" with no response) —
  //    usually means CORS, DNS, or the API is unreachable. The raw axios
  //    message is not user-friendly, so override it.
  const rawMsg = err?.message || '';
  if (/network error/i.test(rawMsg) || err?.code === 'ERR_NETWORK') {
    return "Couldn't reach our servers. Please check your connection and try again.";
  }

  // 4) Strip the noisy "Firebase: Error (auth/...)." prefix if it slipped
  //    through (e.g. an auth code we don't have mapped yet).
  if (rawMsg.startsWith('Firebase:')) {
    return 'Sign-in failed. Please try again or contact support.';
  }

  // 5) Last resort — return the raw message if it looks user-friendly,
  //    otherwise a generic fallback.
  if (rawMsg && rawMsg.length < 140) return rawMsg;
  return 'Something went wrong. Please try again.';
}

export default function AuthPage() {
  const navigate = useNavigate();
  const { login, signup, loginWithGoogle, loginWithApple } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    mobile_number: '',
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);

  // Format mobile number with country code
  const formatMobileNumber = (value) => {
    let cleaned = value.replace(/[^\d+]/g, '');
    if (!cleaned.startsWith('+')) {
      if (cleaned.startsWith('91') && cleaned.length > 2) {
        cleaned = '+' + cleaned;
      } else if (cleaned.length > 0) {
        cleaned = '+91' + cleaned;
      }
    }
    return cleaned;
  };

  const handleMobileChange = (e) => {
    const formatted = formatMobileNumber(e.target.value);
    if (formatted.length <= 13) {
      setFormData({ ...formData, mobile_number: formatted });
    }
  };

  const validateMobileNumber = (number) => {
    const regex = /^\+91\d{10}$/;
    return regex.test(number);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isLogin && !validateMobileNumber(formData.mobile_number)) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }
    if (!isLogin && !agreedToTerms) {
      toast.error('Please agree to the Terms of Service and Privacy Policy to continue.');
      return;
    }
    setLoading(true);
    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Welcome back!');
      } else {
        await signup(
          formData.email,
          formData.password,
          formData.name,
          formData.mobile_number
        );
        toast.success('Account created successfully!');
      }
      navigate('/events');
    } catch (error) {
      console.error('Auth error:', error);
      toast.error(friendlyError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    if (!isLogin && !agreedToTerms) {
      toast.error('Please agree to the Terms of Service and Privacy Policy to continue.');
      return;
    }
    setLoading(true);
    try {
      await loginWithGoogle();
      toast.success('Signed in with Google');
      navigate('/events');
    } catch (error) {
      console.error('Google sign-in error:', error);
      toast.error(friendlyError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    if (!isLogin && !agreedToTerms) {
      toast.error('Please agree to the Terms of Service and Privacy Policy to continue.');
      return;
    }
    setLoading(true);
    try {
      await loginWithApple();
      toast.success('Signed in with Apple');
      navigate('/events');
    } catch (error) {
      console.error('Apple sign-in error:', error);
      // Common: user cancelled, native plugin missing, popup blocked.
      const msg = error?.message || friendlyError(error);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center px-4 py-12" style={{ fontFamily: "'Outfit', sans-serif" }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="flex items-center justify-center mb-4">
            <img
              src={BRAND_LOGO}
              alt="BYARD Logo"
              className="h-24 w-auto object-contain"
              data-testid="brand-logo"
            />
          </div>
          <img
            src={BRAND_ACRONYM}
            alt="BYARD"
            className="h-10 mx-auto object-contain"
            data-testid="brand-acronym"
          />
          <p data-testid="auth-subtitle" className="text-gray-500 text-sm mt-3">
            Connect through events, shop, create
          </p>
        </div>

        <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
          <div className="flex gap-2 mb-6">
            <button
              data-testid="login-tab"
              type="button"
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-2 rounded-full font-bold text-sm ${
                isLogin ? 'bg-[#2B3A67] text-white' : 'bg-gray-100 text-gray-500'
              }`}
            >
              Login
            </button>
            <button
              data-testid="register-tab"
              type="button"
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-2 rounded-full font-bold text-sm ${
                !isLogin ? 'bg-[#E54562] text-white' : 'bg-gray-100 text-gray-500'
              }`}
            >
              Register
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <div>
                  <label htmlFor="name" className="text-xs font-bold uppercase tracking-widest text-gray-400">
                    Name
                  </label>
                  <input
                    id="name"
                    data-testid="name-input"
                    type="text"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required={!isLogin}
                    className="mt-2 w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#E54562]"
                  />
                </div>

                <div>
                  <label htmlFor="mobile" className="text-xs font-bold uppercase tracking-widest text-gray-400">
                    Mobile Number <span className="text-[#E54562]">*</span>
                  </label>
                  <input
                    id="mobile"
                    data-testid="mobile-input"
                    type="tel"
                    placeholder="+91 98765 43210"
                    value={formData.mobile_number}
                    onChange={handleMobileChange}
                    required={!isLogin}
                    className="mt-2 w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#E54562]"
                  />
                  <p className="text-[10px] text-gray-400 mt-1">This will be your primary identifier for OTP verification</p>
                </div>
              </>
            )}

            <div>
              <label htmlFor="email" className="text-xs font-bold uppercase tracking-widest text-gray-400">
                Email
              </label>
              <input
                id="email"
                data-testid="email-input"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="mt-2 w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#E54562]"
              />
            </div>

            <div>
              <label htmlFor="password" className="text-xs font-bold uppercase tracking-widest text-gray-400">
                Password
              </label>
              <input
                id="password"
                data-testid="password-input"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="mt-2 w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#E54562]"
              />
              {isLogin && (
                <div className="mt-2 text-right">
                  <Link
                    to="/forgot-password"
                    data-testid="forgot-password-link"
                    className="text-xs text-[#E54562] hover:underline font-medium"
                  >
                    Forgot Password?
                  </Link>
                </div>
              )}
            </div>

            <button
              data-testid="auth-submit-button"
              type="submit"
              disabled={loading || (!isLogin && !agreedToTerms)}
              className="w-full py-4 rounded-2xl bg-[#E54562] text-white font-extrabold shadow-lg disabled:opacity-60"
            >
              {loading ? 'Please wait...' : isLogin ? 'Login' : 'Create Account'}
            </button>

            {!isLogin && (
              <label className="flex items-start gap-2 mt-3 cursor-pointer" data-testid="terms-consent-row">
                <input
                  type="checkbox"
                  data-testid="terms-consent-checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-1"
                />
                <span className="text-[11px] leading-snug text-gray-500">
                  I agree to the{' '}
                  <Link to="/legal/terms" target="_blank" rel="noreferrer" className="underline text-[#2B3A67] font-semibold">Terms of Service</Link>
                  {' '}and{' '}
                  <Link to="/legal/privacy" target="_blank" rel="noreferrer" className="underline text-[#2B3A67] font-semibold">Privacy Policy</Link>.
                </span>
              </label>
            )}
          </form>

          <div className="my-5 flex items-center gap-3 text-xs text-gray-400">
            <span className="flex-1 h-px bg-gray-200" />
            OR
            <span className="flex-1 h-px bg-gray-200" />
          </div>

          <button
            data-testid="google-signin-button"
            type="button"
            onClick={handleGoogleSignIn}
            disabled={loading || (!isLogin && !agreedToTerms)}
            className="w-full py-3 rounded-2xl bg-white border-2 border-gray-200 text-gray-700 font-bold shadow-sm flex items-center justify-center gap-3 hover:border-gray-300 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5" viewBox="0 0 48 48">
              <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
              <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
              <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
              <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
            </svg>
            Continue with Google
          </button>

          {shouldShowAppleButton() && (
            <button
              data-testid="apple-signin-button"
              type="button"
              onClick={handleAppleSignIn}
              disabled={loading || (!isLogin && !agreedToTerms)}
              className="mt-3 w-full py-3 rounded-2xl bg-black text-white font-bold shadow-sm flex items-center justify-center gap-3 hover:bg-gray-900 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M16.365 1.43c0 1.14-.466 2.246-1.226 3.04-.81.85-2.122 1.51-3.222 1.42-.135-1.115.444-2.282 1.16-3.038C13.92 1.93 15.205 1.41 16.365 1.43zM20.43 17.31c-.34.79-.508 1.144-.94 1.84-.604.971-1.456 2.18-2.51 2.19-.937.01-1.18-.61-2.45-.6-1.27.01-1.54.61-2.48.6-1.054-.01-1.86-1.1-2.466-2.07-1.69-2.71-1.866-5.89-.825-7.58.74-1.2 1.91-1.9 3-1.9 1.13 0 1.84.62 2.78.62.91 0 1.46-.62 2.77-.62.99 0 2.04.54 2.78 1.46-2.45 1.34-2.05 4.84.34 6.06z"/>
              </svg>
              Continue with Apple
            </button>
          )}
        </div>

        {/* Legal links */}
        <div className="mt-6 text-center" data-testid="auth-legal-links">
          <p className="text-[10px] text-gray-400 leading-relaxed">
            By using Backyard you agree to our{' '}
            <Link to="/legal/terms" className="underline hover:text-[#2B3A67]">Terms</Link>,{' '}
            <Link to="/legal/privacy" className="underline hover:text-[#2B3A67]">Privacy Policy</Link>,{' '}
            <Link to="/legal/refund" className="underline hover:text-[#2B3A67]">Refund Policy</Link>{' '}
            and{' '}
            <Link to="/legal/community-guidelines" className="underline hover:text-[#2B3A67]">Community Guidelines</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
